import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import AsyncSelect, { AsyncSelectProps } from 'components/AsyncSelect';
import {
  AirportSelect_data$data,
  AirportSelect_data$key,
} from '__generated__/AirportSelect_data.graphql';
import Query from '__generated__/AirportsRefetchQuery.graphql';
import { useField } from 'formik';

export type Option = {
  value: string;
  label: string;
  code: string;
};

type Props = Omit<
  AsyncSelectProps<Option>,
  'fragment' | 'getOptions' | 'query'
> & {
  data: AirportSelect_data$key;
  name: string;
};

type Airport = {
  id: string;
  name: string;
  code: string;
};

export const makeOption = ({ id, name, ...rest }: Airport) => ({
  value: id,
  label: name,
  ...rest,
});

const getOptions = (data: AirportSelect_data$data) =>
  data.airports.map(makeOption);

const optionEqual = (option: Option, value: Option) =>
  option.value === value.value;

const getOptionLabel = ({ label, code }: Option) => `[${code}] ${label}`;

const AirportSelect: FC<Props> = ({ name, helperText, ...props }) => {
  const [{ value }, meta, { setValue }] = useField(name);
  const hasError = Boolean(meta.touched && meta.error);

  const handleChange = useCallback(
    (_, value) => {
      setValue(value);
    },
    [setValue]
  );

  return (
    <AsyncSelect<Option>
      query={Query}
      isOptionEqualToValue={optionEqual}
      getOptionLabel={getOptionLabel}
      getOptions={getOptions}
      value={value}
      onChange={handleChange}
      error={hasError}
      helperText={hasError ? meta.error : helperText}
      fragment={graphql`
        fragment AirportSelect_data on Query
        @argumentDefinitions(search: { type: "String" })
        @refetchable(queryName: "AirportsRefetchQuery") {
          airports(first: 10, search: $search) {
            id
            name
            code
          }
        }
      `}
      {...props}
    />
  );
};

export default AirportSelect;
